import { useCallback, useEffect, useState } from "react";
import { Typography, Box, Snackbar, Alert, Stack } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";

import CardPaciente from "../../components/CardPaciente";
import api from "../../services/api";
import { verificaPermissaoDeAcesso } from "../../services/permissoesDeAcesso";
import CardBaseMonitoring from "../../components/CardBaseMonitoring";
import ButtonGoBack from "../../components/Buttons/outlinedGoback";
import LineChartMonitoring from "../../components/LineChartMonitoring";
import Loading from "../../components/Loading";

import { formatDate, formatDateISO8601 } from "../../utils/formatarData";
import { FieldDatePicker } from "../../components/Fields";
import { ReactComponent as IconInfo } from "../../images/dialogs/info.svg";
import dayjs from "dayjs";
import "./styles.css";
import {
  bgColors,
  formatDateSensor,
  getNameSensor,
  getStatus,
  isCheckData,
  labelStatus,
} from "./checkData";
import DetailModal from "./DetailModal";
import { getItemDecrypt } from "../../utils/cryptoData";

const ItemCard = ({ title, subtitle, value, color = "#063F5E" }) => (
  <Box display="flex" justifyContent="space-between">
    <Box>
      <Typography
        className="title"
        sx={{ fontSize: "17px", lineHeight: "10px" }}
      >
        {title}
      </Typography>
      <Typography sx={{ color: "#737373" }}>{subtitle}</Typography>
    </Box>
    <Typography className="title" sx={{ fontSize: "40px", color: color }}>
      {value}
    </Typography>
  </Box>
);

export default function MonitoramentoAnjo() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dataSensors, setDataSensors] = useState([]);
  const [filterDate, SetFilterDate] = useState(formatDateISO8601(new Date()));
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedSensor, setSelectedSensor] = useState({
    sensor: "",
    data: null,
  });

  let vinculo = getItemDecrypt("vinculo_ativo");

  function hasNumber(string) {
    return /\d/.test(string);
  }

  useEffect(() => {
    checarPermissaoDeAcesso();
    if (filterDate === null || hasNumber(filterDate)) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate]);

  function handleShowDetailsGraphic(data, sensorName) {
    setSelectedSensor({
      sensor: sensorName,
      data: data,
    });
    setOpenModal(true);
  }
  const RenderGraphic = useCallback(
    ({ data, sensor, group, fetchData }) => (
      <CardBaseMonitoring
        title={sensor}
        titleVal=""
        status={labelStatus[getStatus(sensor, group.valores)]}
        statusColor={bgColors[getStatus(sensor, group.valores)]}
        lastUpdate={data[data.length - 1].date}
        funcRefresh={() => fetchData()}
        funcDetail={() => handleShowDetailsGraphic(data, sensor)}
      >
        <Box sx={{ padding: "0px 15px", marginBottom: "20px" }}>
          <LineChartMonitoring
            data={data}
            KeyXAxis="date"
            KeyYAxis="value"
            lineColor="#063F5E"
            lineStrokeWidth={1}
          />
        </Box>
        {!!group.valores && (
          <ItemCard
            title={"BPM"}
            subtitle={"Frequência cardíaca"}
            value={group.valores}
            color={bgColors[getStatus("BPM", group.valores)]}
          />
        )}
      </CardBaseMonitoring>
    ),
    []
  );

  async function fetchData() {
    try {
      setLoading(true);
      const { data } = await api.post(`/dados-sensores/${id}`, {
        filtrarData: filterDate,
      });
      if (data?.error) {
        throw new Error(data?.error);
      }
      setErrorMessage(null);
      const dataCheckData = isCheckData(data);
      const response = formatDateSensor(dataCheckData);
      setDataSensors(response);
    } catch (error) {
      setError({
        children: `${error}`,
        severity: "error",
      });
      setErrorMessage(`${error}`);
    } finally {
      setLoading(false);
    }
  }

  async function checarPermissaoDeAcesso() {
    try {
      setLoading(true);

      const permiteAcesso = await verificaPermissaoDeAcesso(id, vinculo.id);
      if (permiteAcesso) {
        fetchData();
      } else {
        navigate(`/pacientes/`);
      }
    } catch (error) {
      console.error("Ocorreu um erro:", error.message);
    }
  }

  return (
    <Box component="div" id="container_monitoramento_anjo">
      <Box component="div" className="container_title">
        <ButtonGoBack />
        <Box component="h1" className="title">
          Monitoramento ANJO
        </Box>
      </Box>
      <CardPaciente id={id} />

      <Box component="div" className="container">
        <Stack minWidth="230px" maxWidth="300px" flex={1} marginBottom="24px">
          <FieldDatePicker
            label="Filtrar por data"
            value={filterDate}
            setValue={(value) => {
              SetFilterDate(
                formatDateISO8601(value === null ? new Date() : value)
              );
            }}
            defaultValue={null}
            maxDate={dayjs(new Date())}
          />
        </Stack>
        {loading ? (
          <Loading />
        ) : (
          <Box className="Grid_monitoramento_anjo">
            {/* <CardBaseMonitoring
              title="Pressão Arterial"
              status="Normal"
              lastUpdate={lastUpdated}
              statusColor={bgColors["normal"]}
              funcRefresh={() => {}}
            >
              <Box display="flex" flexDirection="column" gap="10px">
                <ItemCard title="Sys" subtitle="mmHg" value="120" />
                <ItemCard title="Sys" subtitle="mmHg" value="80" />
              </Box>
            </CardBaseMonitoring> 
            */}
            {Object.keys(dataSensors).length > 0 ? (
              <>
                {Object.entries(dataSensors).map(([sensor, value], index) => {
                  const { data, group } = dataSensors[sensor];
                  if (data.length > 1) {
                    return (
                      <RenderGraphic
                        key={`${sensor}-${index}`}
                        sensor={sensor}
                        fetchData={fetchData}
                        group={group}
                        data={data}
                      />
                    );
                  } else {
                    return (
                      <CardBaseMonitoring
                        key={`${sensor}-${index}`}
                        title={getNameSensor(sensor)}
                        subTitle="Saturação do oxigênio"
                        status={labelStatus[getStatus(sensor, data[0].value)]}
                        statusColor={bgColors[getStatus(sensor, data[0].value)]}
                        lastUpdate={data[0].date}
                        funcRefresh={() => {}}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Typography
                            className="title"
                            sx={{
                              fontSize: "40px",
                              padding: "20px 0px",
                              color: bgColors[getStatus(sensor, data[0].value)],
                            }}
                          >
                            {data[0].value} %
                          </Typography>
                        </Box>
                      </CardBaseMonitoring>
                    );
                  }
                })}
              </>
            ) : (
              <Box component="div" className="containerFooter">
                {errorMessage !== null ? (
                  <Box
                    sx={{
                      "svg path": { stroke: "red" },
                      "svg path:not(:first-of-type)": { fill: "red" },
                    }}
                  >
                    <IconInfo />
                  </Box>
                ) : (
                  <IconInfo />
                )}

                <Box>
                  <Typography>
                    {errorMessage !== null ? (
                      errorMessage
                    ) : (
                      <>
                        Desculpe, os dados do paciente não foram encontrados ou
                        a data do filtro não corresponde a nenhum registro de
                        paciente em nosso sistema. Por favor, verifique os dados
                        tente e novamente.
                      </>
                    )}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      color: "#063F5E",
                      marginTop: "5px",
                    }}
                  >
                    <strong>Data selecionada: {formatDate(filterDate)}</strong>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <DetailModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        data={selectedSensor.data}
        sensor={selectedSensor.sensor}
      />
      {!!error && (
        <Snackbar
          open
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert {...error} onClose={() => setError(null)} />
        </Snackbar>
      )}
    </Box>
  );
}
